import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Search, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

const TEMPLATE_FORM_DEFAULTS = {
  template_name: "",
  case_type: "Appeal",
  order_type: "Final Order",
  applicable_authority: "Special Secretary (Revenue)",
  language: "Gujarati",
  version_no: "1",
  template_status: "Approved",
};

const TEMPLATE_FIELD_LABELS = {
  template_name: "Template name",
  case_type: "Case type",
  order_type: "Order type",
  applicable_authority: "Applicable authority",
  language: "Language",
  version_no: "Version",
  template_status: "Status",
};

const STANDARD_TEMPLATE_SECTIONS = [
  { section_key: "government_header", section_name: "સરકારી શીર્ષક / કચેરી હેડર", is_mandatory: true },
  { section_key: "case_details", section_name: "કેસ નંબર અને કેસની વિગતો", is_mandatory: true },
  { section_key: "parties", section_name: "પક્ષકારોની વિગતો", is_mandatory: true },
  { section_key: "subject", section_name: "વિષય", is_mandatory: true },
  { section_key: "lower_authority_order", section_name: "નીચલા સત્તાધિકારી હુકમનો સંદર્ભ", is_mandatory: true },
  { section_key: "brief_facts", section_name: "ટૂંકી હકીકતો", is_mandatory: true },
  { section_key: "submissions", section_name: "રજૂઆતો / દલીલો", is_mandatory: true },
  { section_key: "documents_considered", section_name: "ધ્યાનમાં લેવાયેલા દસ્તાવેજો અને સંદર્ભો", is_mandatory: true },
  { section_key: "observations", section_name: "નિરીક્ષણ / કારણો (ડ્રાફ્ટ)", is_mandatory: true },
  { section_key: "operative_order", section_name: "અંતિમ ઓપરેટિવ આદેશ (ડ્રાફ્ટ)", is_mandatory: true },
  { section_key: "signature_block", section_name: "સહી / હોદ્દો / તારીખ", is_mandatory: true },
];

const KINDS = [
  { key: "templates", label: "Order Templates", fields: [
    { k: "template_name", l: "Name" }, { k: "case_type", l: "Case Type" },
    { k: "order_type", l: "Order Type" }, { k: "applicable_authority", l: "Authority" },
    { k: "language", l: "Language" }, { k: "version_no", l: "Version" },
    { k: "template_status", l: "Status" },
  ], create: ["template_name", "case_type", "order_type", "applicable_authority", "language", "version_no", "template_status"] },
  { key: "grs", label: "GR References", fields: [
    { k: "gr_number", l: "GR No." }, { k: "gr_date", l: "Date" },
    { k: "subject", l: "Subject" }, { k: "applicable_area", l: "Area" }, { k: "validity_status", l: "Validity" },
  ], create: ["gr_number", "gr_date", "department_name", "subject", "applicable_area", "summary", "validity_status"] },
  { key: "citations", label: "Legal Citations", fields: [
    { k: "citation_title", l: "Title" }, { k: "court_or_authority", l: "Court" },
    { k: "citation_number", l: "Citation No." }, { k: "citation_date", l: "Date" }, { k: "legal_topic", l: "Topic" },
  ], create: ["citation_title", "court_or_authority", "citation_number", "citation_date", "legal_topic", "summary", "relevance_notes"] },
  { key: "terms", label: "Gujarati Legal Terms", fields: [
    { k: "term_gujarati", l: "Gujarati", guj: true }, { k: "term_english", l: "English" },
    { k: "term_category", l: "Category" }, { k: "approved_status", l: "Status" },
  ], create: ["term_gujarati", "term_english", "term_category", "standard_usage", "alternative_terms", "approved_status"] },
  { key: "clauses", label: "Reusable Clauses", fields: [
    { k: "clause_title", l: "Title" }, { k: "clause_category", l: "Category" },
    { k: "clause_text_gujarati", l: "Gujarati Text", guj: true, truncate: 80 },
    { k: "approval_status", l: "Status" }, { k: "version_no", l: "Version" },
  ], create: ["clause_title", "clause_category", "clause_text_gujarati", "usage_condition", "approval_status", "version_no"] },
];

export default function KnowledgeBase() {
  const [active, setActive] = useState("templates");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [templateSections, setTemplateSections] = useState(
    () => STANDARD_TEMPLATE_SECTIONS.map((s, i) => ({ ...s, section_sequence: i + 1 })),
  );

  const reload = () => {
    api.get(`/knowledge/${active}`, { params: search ? { search } : {} }).then((r) => setItems(r.data));
  };
  useEffect(() => { setSearch(""); reload(); }, [active]); // eslint-disable-line

  const def = KINDS.find((k) => k.key === active);

  const create = async (e) => {
    e.preventDefault();
    try {
      if (active === "templates") {
        if (!form.template_name?.trim()) {
          toast.error("Template name is required");
          return;
        }
        if (!templateSections.length) {
          toast.error("Add at least one section menu");
          return;
        }
        const missing = templateSections.findIndex((s) => !s.section_name?.trim());
        if (missing >= 0) {
          toast.error(`Section ${missing + 1} needs a Gujarati menu label`);
          return;
        }
        await api.post("/knowledge/templates", {
          ...form,
          sections: templateSections.map((s, i) => ({
            ...s,
            section_sequence: i + 1,
          })),
        });
      } else {
        await api.post(`/knowledge/${active}`, form);
      }
      toast.success("Created");
      setOpen(false);
      setForm({});
      setTemplateSections(STANDARD_TEMPLATE_SECTIONS.map((s, i) => ({ ...s, section_sequence: i + 1 })));
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openAddDialog = () => {
    if (active === "templates") {
      setForm({ ...TEMPLATE_FORM_DEFAULTS });
      setTemplateSections(STANDARD_TEMPLATE_SECTIONS.map((s, i) => ({ ...s, section_sequence: i + 1 })));
    } else {
      setForm({});
    }
    setOpen(true);
  };

  const addTemplateSection = () => {
    setTemplateSections((prev) => [
      ...prev,
      { section_key: `section_${prev.length + 1}`, section_name: "", is_mandatory: true },
    ]);
  };

  const updateTemplateSection = (index, field, value) => {
    setTemplateSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const removeTemplateSection = (index) => {
    setTemplateSections((prev) => prev.filter((_, i) => i !== index));
  };

  const moveTemplateSection = (index, dir) => {
    setTemplateSections((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  return (
    <Layout breadcrumbs={["SSRD", "Knowledge Base"]}>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Knowledge and Reference</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="kb-title">Knowledge Base</h1>
          <p className="text-sm text-muted-foreground">Templates, GRs, citations, Gujarati legal terms and reusable clauses.</p>
        </div>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="grid grid-cols-5">
          {KINDS.map((k) => <TabsTrigger key={k.key} value={k.key} data-testid={`kb-tab-${k.key}`}>{k.label}</TabsTrigger>)}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key}>
            <Card><CardContent className="p-0">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") reload(); }} data-testid={`kb-search-${k.key}`} />
                </div>
                <Button className="gap-2" data-testid={`kb-add-${k.key}`} onClick={openAddDialog}>
                  <Plus className="size-4" /> Add
                </Button>
                <Dialog open={open && active === k.key} onOpenChange={setOpen}>
                  {k.key === "templates" ? (
                    <DialogContent className="max-w-3xl p-0 gap-0 flex flex-col max-h-[90vh]" data-testid="kb-template-dialog">
                      <form onSubmit={create} className="flex flex-col min-h-0 flex-1">
                        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0 text-left">
                          <DialogTitle>New order template</DialogTitle>
                          <DialogDescription>
                            Define template details and the section menus that appear when drafting and reviewing orders.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 min-h-0">
                          <section>
                            <h3 className="text-sm font-semibold mb-3">Template details</h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="sm:col-span-2">
                                <Label htmlFor="tpl-name" className="text-xs">Template name <span className="text-red-600">*</span></Label>
                                <Input
                                  id="tpl-name"
                                  className="mt-1"
                                  placeholder="e.g. SSRD Appeal — Standard Order"
                                  value={form.template_name || ""}
                                  onChange={(e) => setForm({ ...form, template_name: e.target.value })}
                                  data-testid="kb-template-name"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Case type</Label>
                                <Input
                                  className="mt-1"
                                  value={form.case_type || ""}
                                  onChange={(e) => setForm({ ...form, case_type: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Order type</Label>
                                <Input className="mt-1" value={form.order_type || ""} onChange={(e) => setForm({ ...form, order_type: e.target.value })} />
                              </div>
                              <div>
                                <Label className="text-xs">Applicable authority</Label>
                                <Input className="mt-1" value={form.applicable_authority || ""} onChange={(e) => setForm({ ...form, applicable_authority: e.target.value })} />
                              </div>
                              <div>
                                <Label className="text-xs">Language</Label>
                                <Input className="mt-1" value={form.language || ""} onChange={(e) => setForm({ ...form, language: e.target.value })} />
                              </div>
                              <div>
                                <Label className="text-xs">Version</Label>
                                <Input className="mt-1" type="number" min={1} value={form.version_no ?? "1"} onChange={(e) => setForm({ ...form, version_no: e.target.value })} />
                              </div>
                              <div>
                                <Label className="text-xs">Status</Label>
                                <Select value={form.template_status || "Approved"} onValueChange={(v) => setForm({ ...form, template_status: v })}>
                                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {["Draft", "Approved", "Archived"].map((v) => (
                                      <SelectItem key={v} value={v}>{v}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </section>
                          <section>
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                              <div>
                                <h3 className="text-sm font-semibold">Section menus</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Order of tabs in draft generation and review ({templateSections.length} sections)
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setTemplateSections(
                                    STANDARD_TEMPLATE_SECTIONS.map((s, i) => ({ ...s, section_sequence: i + 1 })),
                                  )}
                                  data-testid="kb-template-load-standard"
                                >
                                  Use standard SSRD (11)
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={addTemplateSection} data-testid="kb-template-add-section">
                                  <Plus className="size-3.5 mr-1" /> Add section
                                </Button>
                              </div>
                            </div>
                            <div className="rounded-md border border-border overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                                    <TableHead className="w-10 h-9 text-xs">#</TableHead>
                                    <TableHead className="h-9 text-xs">Menu label (Gujarati)</TableHead>
                                    <TableHead className="w-[140px] h-9 text-xs">Section key</TableHead>
                                    <TableHead className="w-16 h-9 text-xs text-center">Req.</TableHead>
                                    <TableHead className="w-[100px] h-9 text-xs text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {templateSections.length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                                        No sections — add one or load the standard set.
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  {templateSections.map((sec, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="text-xs text-muted-foreground font-mono py-2">{idx + 1}</TableCell>
                                      <TableCell className="py-2">
                                        <Input
                                          className="gujarati h-9"
                                          placeholder="ગુજરાતી શીર્ષક"
                                          value={sec.section_name}
                                          onChange={(e) => updateTemplateSection(idx, "section_name", e.target.value)}
                                          data-testid={`kb-section-name-${idx}`}
                                        />
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <Input
                                          className="h-9 font-mono text-xs"
                                          placeholder="section_key"
                                          value={sec.section_key || ""}
                                          onChange={(e) => updateTemplateSection(idx, "section_key", e.target.value)}
                                        />
                                      </TableCell>
                                      <TableCell className="py-2 text-center">
                                        <Checkbox
                                          checked={sec.is_mandatory !== false}
                                          onCheckedChange={(v) => updateTemplateSection(idx, "is_mandatory", !!v)}
                                          aria-label="Mandatory section"
                                        />
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <div className="flex items-center justify-end gap-0.5">
                                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveTemplateSection(idx, -1)} disabled={idx === 0} aria-label="Move up">
                                            <ArrowUp className="size-4" />
                                          </Button>
                                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveTemplateSection(idx, 1)} disabled={idx === templateSections.length - 1} aria-label="Move down">
                                            <ArrowDown className="size-4" />
                                          </Button>
                                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-700" onClick={() => removeTemplateSection(idx)} aria-label="Remove">
                                            <Trash2 className="size-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </section>
                        </div>
                        <DialogFooter className="px-6 py-4 border-t border-border shrink-0 gap-2 sm:gap-2">
                          <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                          <Button type="submit" disabled={!form.template_name?.trim()} data-testid="kb-template-save">Save template</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  ) : (
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Add {k.label}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={create} className="space-y-3">
                        {k.create.map((field) => (
                          <div key={field}>
                            <Label className="text-xs">{TEMPLATE_FIELD_LABELS[field] || field.replace(/_/g, " ")}</Label>
                            {field.includes("summary") || field.includes("text") || field.includes("usage") || field.includes("notes") ? (
                              <Textarea rows={3} className="mt-1" value={form[field] || ""} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                            ) : (
                              <Input className="mt-1" value={form[field] || ""} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                            )}
                          </div>
                        ))}
                        <DialogFooter className="pt-2">
                          <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                          <Button type="submit">Save</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  )}
                </Dialog>
              </div>
              <Table>
                <TableHeader><TableRow>{def.fields.map((f) => <TableHead key={f.k}>{f.l}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {items.length === 0 && <TableRow><TableCell colSpan={def.fields.length} className="text-center text-sm text-muted-foreground py-8">No entries.</TableCell></TableRow>}
                  {items.map((it, idx) => (
                    <TableRow key={idx}>
                      {def.fields.map((f) => (
                        <TableCell key={f.k} className={f.guj ? "gujarati text-sm max-w-md" : "text-xs"}>
                          {f.truncate ? (it[f.k] || "").slice(0, f.truncate) + ((it[f.k] || "").length > f.truncate ? "…" : "") : it[f.k] || "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>
    </Layout>
  );
}
